import { useQuery } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DashboardTrendMetric, DashboardTrendRange } from '@bhops/shared';
import { dashboardApi } from '@/api/dashboard';
import { extractApiError } from '@/api/client';

interface Props {
  metric: DashboardTrendMetric;
  range: DashboardTrendRange;
  title: string;
  /** Display value as percentage (×100, append %). For ratio metrics 0..1. */
  asPercent?: boolean;
  /** Line stroke color override. */
  color?: string;
}

const DEFAULT_COLOR = 'var(--brand)';

export function TrendLineChart({ metric, range, title, asPercent, color }: Props) {
  const q = useQuery({
    queryKey: ['dashboard', 'trends', metric, range],
    queryFn: () => dashboardApi.trend(metric, range),
    staleTime: 60_000,
  });

  // recharts expects an array of objects with `date` and `value` keys.
  const data = (q.data?.buckets ?? []).map((b) => ({
    date: b.date,
    value: asPercent ? b.value * 100 : b.value,
  }));

  return (
    <div style={chartCard}>
      <div className="kuun-label" style={{ marginBottom: 8 }}>
        {title}
      </div>
      {q.isLoading ? (
        <div style={placeholderStyle}>불러오는 중…</div>
      ) : q.error ? (
        <div style={{ ...placeholderStyle, color: 'var(--fg-danger)' }}>
          {extractApiError(q.error)}
        </div>
      ) : data.length === 0 ? (
        <div style={placeholderStyle}>데이터 없음</div>
      ) : (
        <div data-testid={`trend-chart-${metric}-${range}`} style={{ width: '100%', height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="var(--border-default)" strokeDasharray="2 4" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'var(--fg-muted)' }}
                tickFormatter={shortDate}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: 'var(--fg-muted)' }}
                width={36}
                tickFormatter={(v) => (asPercent ? `${v.toFixed(0)}%` : String(v))}
              />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(v) => {
                  const num = typeof v === 'number' ? v : Number(v);
                  return asPercent ? `${num.toFixed(1)}%` : String(num);
                }}
                labelFormatter={(label) => String(label)}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color ?? DEFAULT_COLOR}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// `2026-05-18` → `5/18` for compact x-axis labels.
function shortDate(date: string): string {
  const [, mm, dd] = date.split('-');
  if (!mm || !dd) return date;
  return `${Number(mm)}/${Number(dd)}`;
}

const chartCard: CSSProperties = {
  // 2026-10-27 오너 "여기도 좀 진하게" — 사업 대시보드 권역별 현황 상자와 같은 진한 테두리(글자색 38%).
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 12,
};

const placeholderStyle: CSSProperties = {
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--fg-muted)',
  fontSize: 12,
};
